import React from 'react'
import { History } from 'history'
import { Provider as ReduxProvider } from 'react-redux'
import { Router } from 'react-router-dom'
import { ApolloProvider } from 'graphql/ApolloProvider'
import store from 'redux/store'

import ConnectedFlagsProvider from 'containers/ConnectedFlagsProvider'
import StripeProvider from 'containers/StripeProvider'
import { LoadingPageProvider } from 'containers/DynamicLoadingPage'
import { EventEmitterProvider } from '@sketch/utils'
import ModalProvider from './ModalManager/ModalProvider'
import { ToastProvider } from '@sketch/toasts'
import { BannerProvider } from 'containers/Banner'
import { DevToolsSettingsProvider } from 'modules/devtools/settings'

import { GlobalStyle, ThemeManager } from '@sketch/global-styles'
import { createLocalStorageSubscription } from 'utils/localStorage'
import { UsageMetricsImport } from 'modules/devtools/usageMetrics/code-split-import'
import { ErrorBoundary } from '@sketch/components'
import GenericErrorView from 'views/GenericErrorView'
import { HubspotChatProvider } from '../utils/hooks/useHubspotChat'
import { selectStyles } from 'components/Select'
import { useExtraApolloLinks } from './useExtraApolloLinks'

interface ProvidersProps {
  history: History
}

// Changes on the authorizations list will refresh non-visible tabs
const localStorageSubscription = createLocalStorageSubscription()
localStorageSubscription.trackStorageChanges('userAllAuthorizations', () => {
  document.hidden && window.location.reload()
})

const Providers: React.FC<ProvidersProps> = ({ children, history }) => {
  const extraLinks = useExtraApolloLinks({ history })
  return (
    <ErrorBoundary>
      <UsageMetricsImport>
        <EventEmitterProvider>
          <ReduxProvider store={store}>
            <ApolloProvider extraLinks={extraLinks}>
              <Router history={history}>
                <DevToolsSettingsProvider>
                  <ConnectedFlagsProvider>
                    <StripeProvider>
                      <BannerProvider>
                        <HubspotChatProvider>
                          <ErrorBoundary fallbackComponent={GenericErrorView}>
                            <ThemeManager>
                              <LoadingPageProvider>
                                <ToastProvider>
                                  <ModalProvider>
                                    <GlobalStyle
                                      userAgent={window.navigator.userAgent}
                                      // TODO: Make styles of Selector component self contained and remove this property
                                      //  see: https://github.com/sketch-hq/Cloud/issues/14547
                                      extraStyles={selectStyles}
                                    />
                                    {children}
                                  </ModalProvider>
                                </ToastProvider>
                              </LoadingPageProvider>
                            </ThemeManager>
                          </ErrorBoundary>
                        </HubspotChatProvider>
                      </BannerProvider>
                    </StripeProvider>
                  </ConnectedFlagsProvider>
                </DevToolsSettingsProvider>
              </Router>
            </ApolloProvider>
          </ReduxProvider>
        </EventEmitterProvider>
      </UsageMetricsImport>
    </ErrorBoundary>
  )
}

export default Providers
